import { useEffect, useState } from 'react';
import _, { cloneDeep, get, isEmpty, uniq, without } from 'lodash';
import moment from 'moment';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { JudgementADDDocumentType } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import {
  getVictimKeys,
  getDahirAmountByVictimKey,
  getCalculatedAmountByBeneficiary,
} from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/utils';
import { IJudgementOnMeritsBeneficiaryInfo } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/typings';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { DATE_FORMAT } from 'constants/global';
import {
  addDaysToDate,
  validationCourtFileNumber,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/utils';
import { Idetails } from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/typings';
import { EMPTY_DETAIL } from 'containers/data-structure';
import { detailsValidator } from 'containers/common/LegalProceedings/JudgementAdd/Forms/ProcedureInformation/form-validation';
import { arrayTranslate } from 'containers/services/translate';
import {
  displayErrorText,
  validateFormFields,
} from 'containers/services/validateForm';
import { useFormatExpertReportState } from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/hooks';
import { VictimType } from 'typings/globalTypes';

export const useDahirAmountByVictimKey = (
  deceasedVictims: String[] = [],
  judgementAddDocuments: JudgementADDDocumentType[],
  dahirAmount: [],
  judgementAddAppeal: any = {},
  injuredVictims: String[] = [],
) => {
  const [dahirAmountByVictimKey, setDahirAmountByVictimKey] = useState<{
    [key: string]: number;
  }>({});

  useDeepCompareEffect(() => {
    const victimkeys = uniq([
      ...getVictimKeys(judgementAddDocuments, false),
      ...getVictimKeys(
        get(judgementAddAppeal, 'judgementAddDocuments', []),
        false,
      ),
    ]);
    const deceasedVictimkeys = uniq(deceasedVictims);

    setDahirAmountByVictimKey(
      getDahirAmountByVictimKey(
        without(uniq([...victimkeys, ...injuredVictims]), null),
        without(uniq(deceasedVictimkeys), null),
        dahirAmount,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [judgementAddDocuments, dahirAmount, judgementAddAppeal]);

  return [dahirAmountByVictimKey, setDahirAmountByVictimKey];
};
export const getBeneficiaries = (judgementAdd: any, jomId: any) => {
  return get(judgementAdd, 'deceasedVictim', []).reduce(
    (result: any, victim: VictimType) => {
      const matches = victim.beneficiaries.reduce(
        (beneficiaryMatches: any, beneficiary: any) => {
          const beneficiaryWithoutCompensation =
            get(beneficiary, 'jomBwc', []).find(
              (item: any) => item.judgementOnMeritsId === jomId,
            ) || {};
          const beneficiaryWithCompensation =
            get(beneficiary, 'jomBeneficiaries', []).find(
              (item: any) => item.judgementOnMeritsId === jomId,
            ) || {};

          if (!isEmpty(beneficiaryWithoutCompensation)) {
            beneficiaryMatches.push({
              beneficiary: {
                ...beneficiary,

                victim: { victimKey: victim?.victimKey, id: victim?.id },
              },
              judgementOnMeritsId: get(
                beneficiaryWithoutCompensation,
                'judgementOnMeritsId',
              ),
              beneficiaryWithoutCompensation,
              isDeletedCompensation: true,
            });
          }
          if (!isEmpty(beneficiaryWithCompensation)) {
            beneficiaryMatches.push({
              beneficiary: {
                ...beneficiary,

                victim: { victimKey: victim?.victimKey, id: victim?.id },
              },
              judgementOnMeritsId: get(
                beneficiaryWithCompensation,
                'judgementOnMeritsId',
              ),
              ...beneficiaryWithCompensation,
              isDeletedCompensation: false,
              beneficiaryIdJom: get(beneficiaryWithCompensation, 'id', ''),
              beneficiaryWithoutCompensation: {},
            });
          }

          return beneficiaryMatches;
        },
        [],
      );

      return result.concat(matches);
    },
    [],
  );
};

export const useBeneficiaries = (
  judgementOnMerits: any,
  judgementAdd: any,
  deceasedJomVictims: number[],
  dahirAmountByVictimKey: any,
  desceasedVictims: any,
  isMultiple: boolean = false,
) => {
  const beneficiariesWithAndWithoutCompensation = getBeneficiaries(
    judgementAdd,
    get(judgementOnMerits, 'judgementOnMerits.jom.id'),
  );
  const [beneficiaries, setBeneficiaries] = useState<any>([]);
  useEffect(() => {
    const beneficiariesList = [] as any;
    if (
      !isEmpty(beneficiariesWithAndWithoutCompensation) &&
      !isEmpty(deceasedJomVictims)
    ) {
      beneficiariesWithAndWithoutCompensation
        .filter((el: any) =>
          isEmpty(deceasedJomVictims)
            ? true
            : deceasedJomVictims.includes(
                Number(get(el, 'beneficiary.victimId')),
              ),
        )
        .map(
          // eslint-disable-next-line array-callback-return
          (element: any) => {
            beneficiariesList.push({
              id: get(element, 'beneficiary.id'),
              firstName: get(element, 'beneficiary.firstName'),
              lastName: get(element, 'beneficiary.lastName'),
              age: get(element, 'beneficiary.age'),
              beneficiaryType: get(element, 'beneficiary.beneficiaryType'),
              victimKey: get(element, 'beneficiary.victim.victimKey'),
              victimId: get(element, 'beneficiary.victimId'),
              funeralFees: get(element, 'funeralFees', null),
              medicalFees: get(element, 'medicalFees', null),
              moralePrejudice: get(element, 'moralePrejudice', null),
              materialPrejudice: get(element, 'materialPrejudice', null),
              judgementAmount: get(element, 'judgementAmount', null),
              beneficiaryIdJom: get(element, 'beneficiaryIdJom'),
              dateOfBirth: get(element, 'beneficiary.dateOfBirth'),
              beneficiaryWithoutCompensation: get(
                element,
                'beneficiaryWithoutCompensation',
                {},
              ),
              nextOfKinKey: get(element, 'beneficiary.nextOfKinKey', undefined),
              isNew: get(element, 'beneficiary.isNew', false),
              handicapIndicator: get(element, 'beneficiary.handicapIndicator'),
              dependentIndicator: get(
                element,
                'beneficiary.dependentIndicator',
              ),
              isDeletedCompensation: get(
                element,
                'isDeletedCompensation',
                false,
              ),
              ...(featureService.hasFeature(FEATURES.GET_DAHIR_AMOUNT_V2)
                ? getCalculatedAmountByBeneficiary(
                    judgementOnMerits,
                    dahirAmountByVictimKey,
                    element,
                    get(element, 'beneficiary.victim.victimKey'),
                  )
                : undefined),
            });
          },
        );
      return setBeneficiaries(beneficiariesList);
    }
    !_.isEmpty(judgementAdd) &&
      !isEmpty(desceasedVictims) &&
      // eslint-disable-next-line array-callback-return
      desceasedVictims
        .filter((el: any) =>
          !isEmpty(deceasedJomVictims) || isMultiple
            ? deceasedJomVictims.includes(el.id)
            : true,
        )
        .map(
          // eslint-disable-next-line array-callback-return
          (victim: { beneficiaries: [] }) => {
            victim.beneficiaries.map(
              // eslint-disable-next-line array-callback-return
              (element: IJudgementOnMeritsBeneficiaryInfo) => {
                beneficiariesList.push({
                  id: element.id,
                  firstName: element.firstName,
                  lastName: element.lastName,
                  age: element.age,
                  beneficiaryType: element.beneficiaryType,
                  dateOfBirth: get(element, 'dateOfBirth'),
                  victimKey: get(victim, 'victimKey', ''),
                  funeralFees: get(element, 'funeralFees', null),
                  medicalFees: get(element, 'medicalFees', null),
                  moralePrejudice: get(element, 'moralePrejudice', null),
                  materialPrejudice: get(element, 'materialPrejudice', null),
                  judgementAmount: get(element, 'judgementAmount', null),
                  handicapIndicator: get(element, 'handicapIndicator'),
                  dependentIndicator: get(element, 'dependentIndicator'),
                  ...(featureService.hasFeature(FEATURES.GET_DAHIR_AMOUNT_V2)
                    ? getCalculatedAmountByBeneficiary(
                        judgementOnMerits,
                        dahirAmountByVictimKey,
                        { beneficiary: element },
                        get(victim, 'victimKey', ''),
                      )
                    : undefined),
                });
              },
            );
          },
        );

    setBeneficiaries(beneficiariesList);
  }, [
    judgementAdd,
    dahirAmountByVictimKey,
    judgementOnMerits['beneficiaries'],
  ]);
  return [beneficiaries, setBeneficiaries];
};

export const useJudgementAdd = (policyInfo: any, intl: any) => {
  const [details, setDetails] = useState<Idetails>(cloneDeep(EMPTY_DETAIL));
  const [
    processInformationValidation,
    setProcessInformationValidation,
  ] = useState<any>({});
  const [messageError, setMessageError] = useState<any>({});
  useEffect(() => {
    const procedureInformationValidation = detailsValidator(details);
    setProcessInformationValidation(procedureInformationValidation);
    const translateArrayErrorMessages = arrayTranslate(intl.formatMessage);
    const { message } = displayErrorText(
      procedureInformationValidation,
      translateArrayErrorMessages(procedureInformationValidation, true),
    );
    setMessageError(message);
    setAreProcedureInformationValidated(
      !!validateFormFields(procedureInformationValidation) &&
        !(
          new Date(
            addDaysToDate(
              get(policyInfo, 'sagisInfoClaim.lossDate', '') || '',
              1,
            ) || '',
          ) > new Date(get(details, 'hearingDate') || '')
        ) &&
        !!validationCourtFileNumber(details.courtFile) &&
        moment(get(details, 'hearingDate')).format(DATE_FORMAT) !==
          'Invalid date',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);
  const [civilPartyDocuments, setCivilPartyDocuments] = useState<any>([]);
  const [
    areProcedureInformationValidated,
    setAreProcedureInformationValidated,
  ] = useState<boolean>(false);
  const [judgementAddDocuments, setJudgementAddDocuments] = useState<
    JudgementADDDocumentType[]
  >([]);
  const [isCivilPartyPresent, setIsCivilPartyPresent] = useState<boolean>(
    false,
  );
  useEffect(() => {
    setIsCivilPartyPresent(!isEmpty(civilPartyDocuments));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [civilPartyDocuments, details, areProcedureInformationValidated]);

  return {
    details,
    setDetails,
    processInformationValidation,
    setProcessInformationValidation,
    messageError,
    civilPartyDocuments,
    setCivilPartyDocuments,
    areProcedureInformationValidated,
    judgementAddDocuments,
    setJudgementAddDocuments,
    isCivilPartyPresent,
  };
};

export const useExpertReport = (
  judgementAddDocuments: any,
  expertiseReport: any,
) => {
  const [expertReportState, setExpertReportState] = useFormatExpertReportState(
    judgementAddDocuments,
    expertiseReport,
  );
  return {
    expertReportState,
    setExpertReportState,
  };
};

export const useJudgementOnMerits = () => {
  const [judgementOnMeritsInfo, setJudgementOnMeritsInfo] = useState({});
  return {
    judgementOnMeritsInfo,
    setJudgementOnMeritsInfo,
  };
};

export const useAppealAdd = (policyInfo: any, intl: any) => {
  const [detailsAppealAdd, setDetailsAppealAdd] = useState<Idetails>(
    cloneDeep(EMPTY_DETAIL),
  );
  const [
    processInformationValidationAppealAdd,
    setProcessInformationValidationAppealAdd,
  ] = useState<any>({});
  const [messageErrorAppealAdd, setMessageErrorAppealAdd] = useState<any>({});
  useEffect(() => {
    const procedureInformationValidationAppealAdd = detailsValidator(
      detailsAppealAdd,
    );
    setProcessInformationValidationAppealAdd(
      procedureInformationValidationAppealAdd,
    );
    const translateArrayErrorMessages = arrayTranslate(intl.formatMessage);
    const { message } = displayErrorText(
      procedureInformationValidationAppealAdd,
      translateArrayErrorMessages(
        procedureInformationValidationAppealAdd,
        true,
      ),
    );
    setMessageErrorAppealAdd(message);
    setAreProcedureInformationValidatedAppealAdd(
      !!validateFormFields(procedureInformationValidationAppealAdd) &&
        !(
          new Date(
            addDaysToDate(
              get(policyInfo, 'sagisInfoClaim.lossDate', '') || '',
              1,
            ) || '',
          ) > new Date(get(detailsAppealAdd, 'hearingDate') || '')
        ) &&
        !!validationCourtFileNumber(detailsAppealAdd.courtFile) &&
        moment(get(detailsAppealAdd, 'hearingDate')).format(DATE_FORMAT) !==
          'Invalid date',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsAppealAdd]);
  const [
    civilPartyDocumentsAppealAdd,
    setCivilPartyDocumentsAppealAdd,
  ] = useState<any>([]);
  const [
    areProcedureInformationValidatedAppealAdd,
    setAreProcedureInformationValidatedAppealAdd,
  ] = useState<boolean>(false);
  const [appealAddDocuments, setAppealAddDocuments] = useState<
    JudgementADDDocumentType[]
  >([]);
  const [
    isCivilPartyPresentAppealAdd,
    setIsCivilPartyPresentAppealAdd,
  ] = useState<boolean>(false);
  useEffect(() => {
    setIsCivilPartyPresentAppealAdd(!isEmpty(civilPartyDocumentsAppealAdd));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    civilPartyDocumentsAppealAdd,
    detailsAppealAdd,
    areProcedureInformationValidatedAppealAdd,
  ]);

  return {
    detailsAppealAdd,
    setDetailsAppealAdd,
    processInformationValidationAppealAdd,
    setProcessInformationValidationAppealAdd,
    messageErrorAppealAdd,
    civilPartyDocumentsAppealAdd,
    setCivilPartyDocumentsAppealAdd,
    areProcedureInformationValidatedAppealAdd,
    appealAddDocuments,
    setAppealAddDocuments,
    isCivilPartyPresentAppealAdd,
  };
};

export const useJudgementOnMeritsAppeal = () => {
  const [
    judgementOnMeritsAppealInfo,
    setJudgementOnMeritsAppealInfo,
  ] = useState({});
  return {
    judgementOnMeritsAppealInfo,
    setJudgementOnMeritsAppealInfo,
  };
};
